/**
 * Design Tokens from Figma Design System
 * https://www.figma.com/design/B9idQlBqFz2jumUzYhMjgT/Cosimo-UX---DESIGN
 *
 * This file contains all design tokens extracted from the Figma Styles page.
 * Use these tokens throughout the application to maintain design consistency.
 */

/**
 * Typography Design Tokens
 */
export const typography = {
  fontFamilies: {
    primary: "Inter, sans-serif",
    secondary: "Figtree, sans-serif",
  },

  fontSizes: {
    xs: "10.5px", // 10.5px - Heading 4, 5, Links Secondary
    "2xs": "11px", // 11px - Heading 4.1, 4.2
    sm: "12px", // 12px - Paragraph
    base: "12.25px", // 12.25px - Links Primary, Placeholder
    md: "14px", // 14px - Heading 3
    "15": "15px", // 15px - Paragraph Lg, Text Link
    "16": "16px", // 16px - Placeholder Lg
    lg: "21px", // 21px - Heading 2
  },

  fontWeights: {
    regular: 400,
    medium: 500,
    semibold: 600,
  },

  lineHeights: {
    tight: "14px", // Heading 4, 5, Links Secondary
    "16.5": "16.5px", // Heading 4.1, 4.2
    normal: "17.5px", // Links Primary
    relaxed: "22.75px", // Heading 3
    "26.25": "26.25px", // Paragraph Lg
    loose: "28px", // Heading 2
    paragraph: 1.2, // Paragraph
  },

  letterSpacing: {
    tighter: "-0.3589px", // Heading 2
    "-0.7125": "-0.7125px", // Placeholder Lg
    "-0.6094": "-0.6094px", // Paragraph Lg
    tight: "-0.1504px", // Heading 3
    normal: "-0.0179px", // Links Primary, Placeholder
    wide: "0.0923px", // Heading 5, Links Secondary
    wider: "0.6173px", // Heading 4
    "0.9445": "0.9445px", // Heading 4.1
    "1.3845": "1.3845px", // Heading 4.2
  },

  textStyles: {
    heading2: {
      fontFamily: "Inter",
      fontSize: "21px",
      fontWeight: 600,
      lineHeight: "28px",
      letterSpacing: "-0.3589px",
    },
    heading3: {
      fontFamily: "Inter",
      fontSize: "14px",
      fontWeight: 500,
      lineHeight: "22.75px",
      letterSpacing: "-0.1504px",
    },
    heading4: {
      fontFamily: "Inter",
      fontSize: "10.5px",
      fontWeight: 500,
      lineHeight: "14px",
      letterSpacing: "0.6173px",
      textTransform: "uppercase" as const,
    },
    heading5: {
      fontFamily: "Inter",
      fontSize: "10.5px",
      fontWeight: 400,
      lineHeight: "14px",
      letterSpacing: "0.0923px",
    },
    linkPrimaryActive: {
      fontFamily: "Inter",
      fontSize: "12.25px",
      fontWeight: 500,
      lineHeight: "17.5px",
      letterSpacing: "-0.0179px",
    },
    linkPrimaryInactive: {
      fontFamily: "Inter",
      fontSize: "12.25px",
      fontWeight: 400,
      lineHeight: "17.5px",
      letterSpacing: "-0.0179px",
    },
    linkSecondaryActive: {
      fontFamily: "Inter",
      fontSize: "10.5px",
      fontWeight: 500,
      lineHeight: "14px",
      letterSpacing: "0.0923px",
    },
    linkSecondaryInactive: {
      fontFamily: "Inter",
      fontSize: "10.5px",
      fontWeight: 400,
      lineHeight: "14px",
      letterSpacing: "0.0923px",
    },
    placeholder: {
      fontFamily: "Inter",
      fontSize: "12.25px",
      fontWeight: 400,
      lineHeight: "normal",
      letterSpacing: "-0.0179px",
    },
    paragraph: {
      fontFamily: "Figtree",
      fontSize: "12px",
      fontWeight: 400,
      lineHeight: 1.2,
      letterSpacing: "-0.24px",
    },
    heading41CapsWide: {
      fontFamily: "Inter",
      fontSize: "11px",
      fontWeight: 400,
      lineHeight: "16.5px",
      letterSpacing: "0.9445px",
      textTransform: "uppercase" as const,
    },
    heading42CapsWider: {
      fontFamily: "Inter",
      fontSize: "11px",
      fontWeight: 400,
      lineHeight: "16.5px",
      letterSpacing: "1.3845px",
      textTransform: "uppercase" as const,
    },
    placeholderLg: {
      fontFamily: "Inter",
      fontSize: "16px",
      fontWeight: 400,
      lineHeight: "normal",
      letterSpacing: "-0.7125px",
    },
    paragraphLg: {
      fontFamily: "Inter",
      fontSize: "15px",
      fontWeight: 400,
      lineHeight: "26.25px",
      letterSpacing: "-0.6094px",
    },
    // Modal-specific text styles
    modalButtonText: {
      fontFamily: "Inter",
      fontSize: "10px",
      fontWeight: 400,
      lineHeight: "15px",
      letterSpacing: "1.1172px",
      textTransform: "uppercase" as const,
    },
    modalInputText: {
      fontFamily: "Inter",
      fontSize: "13px",
      fontWeight: 300,
      lineHeight: "19.5px",
      letterSpacing: "0.3px",
    },
  } as const,

  // Computed text styles that reference the base tokens above
  get computedTextStyles() {
    return {
      heading2: {
        fontFamily: this.fontFamilies.primary,
        fontSize: this.fontSizes.lg,
        fontWeight: this.fontWeights.semibold,
        lineHeight: this.lineHeights.loose,
        letterSpacing: this.letterSpacing.tighter,
      },
      heading3: {
        fontFamily: this.fontFamilies.primary,
        fontSize: this.fontSizes.md,
        fontWeight: this.fontWeights.medium,
        lineHeight: this.lineHeights.relaxed,
        letterSpacing: this.letterSpacing.tight,
      },
      heading4: {
        fontFamily: this.fontFamilies.primary,
        fontSize: this.fontSizes.xs,
        fontWeight: this.fontWeights.medium,
        lineHeight: this.lineHeights.tight,
        letterSpacing: this.letterSpacing.wider,
        textTransform: "uppercase" as const,
      },
      heading5: {
        fontFamily: this.fontFamilies.primary,
        fontSize: this.fontSizes.xs,
        fontWeight: this.fontWeights.regular,
        lineHeight: this.lineHeights.tight,
        letterSpacing: this.letterSpacing.wide,
      },
      linkPrimaryActive: {
        fontFamily: this.fontFamilies.primary,
        fontSize: this.fontSizes.base,
        fontWeight: this.fontWeights.medium,
        lineHeight: this.lineHeights.normal,
        letterSpacing: this.letterSpacing.normal,
      },
      linkPrimaryInactive: {
        fontFamily: this.fontFamilies.primary,
        fontSize: this.fontSizes.base,
        fontWeight: this.fontWeights.regular,
        lineHeight: this.lineHeights.normal,
        letterSpacing: this.letterSpacing.normal,
      },
      linkSecondaryActive: {
        fontFamily: this.fontFamilies.primary,
        fontSize: this.fontSizes.xs,
        fontWeight: this.fontWeights.medium,
        lineHeight: this.lineHeights.tight,
        letterSpacing: this.letterSpacing.wide,
      },
      linkSecondaryInactive: {
        fontFamily: this.fontFamilies.primary,
        fontSize: this.fontSizes.xs,
        fontWeight: this.fontWeights.regular,
        lineHeight: this.lineHeights.tight,
        letterSpacing: this.letterSpacing.wide,
      },
      placeholder: {
        fontFamily: this.fontFamilies.primary,
        fontSize: this.fontSizes.base,
        fontWeight: this.fontWeights.regular,
        lineHeight: "normal",
        letterSpacing: this.letterSpacing.normal,
      },
      paragraph: {
        fontFamily: this.fontFamilies.secondary,
        fontSize: this.fontSizes.sm,
        fontWeight: this.fontWeights.regular,
        lineHeight: this.lineHeights.paragraph,
        letterSpacing: "-0.24px", // This is unique to paragraph, not in the standard letterSpacing
      },
      heading41CapsWide: {
        fontFamily: this.fontFamilies.primary,
        fontSize: this.fontSizes["2xs"],
        fontWeight: this.fontWeights.regular,
        lineHeight: this.lineHeights["16.5"],
        letterSpacing: this.letterSpacing["0.9445"],
        textTransform: "uppercase" as const,
      },
      heading42CapsWider: {
        fontFamily: this.fontFamilies.primary,
        fontSize: this.fontSizes["2xs"],
        fontWeight: this.fontWeights.regular,
        lineHeight: this.lineHeights["16.5"],
        letterSpacing: this.letterSpacing["1.3845"],
        textTransform: "uppercase" as const,
      },
      placeholderLg: {
        fontFamily: this.fontFamilies.primary,
        fontSize: this.fontSizes["16"],
        fontWeight: this.fontWeights.regular,
        lineHeight: "normal",
        letterSpacing: this.letterSpacing["-0.7125"],
      },
      paragraphLg: {
        fontFamily: this.fontFamilies.primary,
        fontSize: this.fontSizes["15"],
        fontWeight: this.fontWeights.regular,
        lineHeight: this.lineHeights["26.25"],
        letterSpacing: this.letterSpacing["-0.6094"],
      },
      // Modal-specific text styles (reusable beyond modals)
      modalButtonText: {
        fontFamily: this.fontFamilies.primary,
        fontSize: "10px",
        fontWeight: this.fontWeights.regular,
        lineHeight: "15px",
        letterSpacing: "1.1172px",
        textTransform: "uppercase" as const,
      },
      modalInputText: {
        fontFamily: this.fontFamilies.primary,
        fontSize: "13px",
        fontWeight: 300,
        lineHeight: "19.5px",
        letterSpacing: "0.3px",
      },
    } as const;
  },
} as const;

/**
 * Color Design Tokens
 */
export const colors = {
  // Stroke Colors
  stroke: {
    pureBlack: "#000000",
    strongBlack: "#030213",
    softBlack: "#0A0A0A",
  },

  // Accent Colors
  accent: {
    accent1: "#000000",
    lightGray: "#D1D5DC",
    veryLightBlueGray: "#E9EBEF",
    veryLightGray: "#F1F1F3",
    lightestGray: "#F3F4F6",
  },

  // Background Colors
  background: {
    white: "#FFFFFF",
    softGray: "#FAFAFA",
  },

  // Text Colors
  text: {
    black: "#000000",
    softBlack: "#0A0A0A",
    softestBlack: "#364153",
    accentMdGray: "#4A5565",
    accentMdDarkGrey: "#4A5565",
    headerMdGray: "#6A7282",
    grayLightest: "#99A1AF",
    grayExtraLightest: "#99A1AF",
    placeholderGray: "#D1D5DC",
    placeholderDark: "#000000",
    floatingLabelInactive: "rgba(0, 0, 0, 0.4)", // 40% black - for floating label inactive state
    white: "#FFFFFF",
  },

  // Divider Colors
  divider: {
    default: "#E9EBEF",
    light: "#F1F1F3",
    divider1: "#0000001a", // Black with 10% opacity
    divider2: "#e5e7eb",
    mediumGray: "#00000033", // Black with 20% opacity
  },

  // Feedback Colors (standardized to Tailwind red palette)
  feedback: {
    errorBackground: "#FEE2E2", // red-100 - Error background
    errorBorder: "#EF4444", // red-500 - Error border
    errorTextPrimary: "#DC2626", // red-600 - Error text
    noticeBackground: "#F5FAF6",
    noticeBorder: "#CFE5D3",
    noticeTextPrimary: "#245233",
  },
} as const;

/**
 * Spacing Design Tokens (common spacing values from the design)
 */
export const spacing = {
  xxs: "4px",
  xs: "8px",
  sm: "12px",
  md: "16px",
  lg: "20px",
  xl: "24px",
  "2xl": "32px",
  "3xl": "48px",
  "4xl": "64px",
} as const;

/**
 * Border Radius (if needed for components)
 */
export const borderRadius = {
  none: "0",
  sm: "2px",
  md: "4px",
  lg: "8px",
  xl: "12px",
  "2xl": "16px",
  full: "9999px",
} as const;

/**
 * Modal Spacing (shared across auth + onboarding modals)
 */
export const modalSpacing = {
  /** Space below all modal content (prevents cramping at modal bottom edge) */
  contentBottomPadding: "clamp(20px, 4vh, 32px)",
  /** Gap between form submit button and OAuth section (Register/Sign In → Continue with Google) */
  formToOauthGap: "20px",
  /** Gap between OAuth buttons (Google, Apple, etc.) */
  oauthButtonGap: "10.5px",
  /** Responsive top padding for modal content */
  contentTopPadding: "clamp(36px, 9vh, 76px)",
  /** Gap between major sections (title → form → oauth → footer) */
  sectionGap: "33px",
  /** Gap between form input fields */
  formFieldGap: "28px",
  /** Padding for bottom section (sign in/sign up footer) */
  footerSectionPadding: "24px",
  /** Gap within footer section (text + link) */
  footerGap: "14px",
  /** Gap between modal title and subtitle (standardized at 24px) */
  headerGap: "24px",

  // Component dimensions
  /** Form input field height */
  inputFieldHeight: "44.5px",
  /** Primary and OAuth button height (standardized) */
  buttonHeight: "38px",
  /** Compact pill/tag button height (for selection chips, brand pills, etc.) */
  pillButtonHeight: "36px",
  /** Sign-in modal content container width */
  signInContentWidth: "340px",
} as const;

/**
 * Chat-specific spacing tokens
 */
export const chatSpacing = {
  /** Horizontal padding for chat result chips (SearchChip, Suggested Brands button) */
  chipPaddingX: "14px",
} as const;

/**
 * z-index (stacking context) tokens
 *
 * Centralize high z-index layers so overlays don't fight each other across components.
 */
export const zIndex = {
  chatBackdrop: 100000,
  chatWindow: 100001,
} as const;

/**
 * Breakpoints (for responsive design)
 */
/**
 * Tailwind-aligned breakpoints for JS media queries.
 * Use these when matching against Tailwind responsive classes (sm:, md:, lg:, etc.)
 *
 * Convention: Use `window.innerWidth < tailwindBreakpoints.md` for mobile detection.
 * This matches our Tailwind CSS breakpoint where `md:` classes apply at 768px+.
 */
export const tailwindBreakpoints = {
  sm: 640,
  md: 768, // Primary mobile breakpoint: < md = mobile, >= md = desktop
  lg: 1024,
  xl: 1280,
  "2xl": 1536,
} as const;

/**
 * Helper function to apply text style
 *
 * Returns computed text styles that reference the base design tokens.
 * This ensures consistency and makes it easy to update globally.
 *
 * @example
 * ```tsx
 * <h2 style={getTextStyle('heading2')}>Title</h2>
 * ```
 */
export function getTextStyle(styleName: keyof typeof typography.textStyles) {
  const style = typography.computedTextStyles[styleName];
  const baseStyle = {
    fontFamily: style.fontFamily,
    fontSize: style.fontSize,
    fontWeight: style.fontWeight,
    lineHeight: style.lineHeight,
    letterSpacing: style.letterSpacing,
  };

  // Add textTransform if it exists (only for heading4)
  if ("textTransform" in style) {
    return {
      ...baseStyle,
      textTransform: style.textTransform,
    };
  }

  return baseStyle;
}

/**
 * Get raw text style values (with hard-coded strings)
 * Use this if you need the exact Figma values as strings
 */
export function getRawTextStyle(styleName: keyof typeof typography.textStyles) {
  return typography.textStyles[styleName];
}

/**
 * Type exports for TypeScript
 */
export type TextStyleName = keyof typeof typography.textStyles;
export type ColorCategory = keyof typeof colors;
export type SpacingValue = keyof typeof spacing;
export type BorderRadiusValue = keyof typeof borderRadius;
export type ModalSpacingKey = keyof typeof modalSpacing;
